import React, { useState, useRef, useEffect, forwardRef } from "react";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import SizeClassCorrespondance from './SizeClassCorrespondance'

export const RegularDropDown = forwardRef(({
  placeholder = "Actie",
  options = [],
  noOptionsMessage = "Geen acties mogelijk",
  onOptionSelect,
  selectedValue,
  setSelectedValue,
  size = "l",
  disabled = false // New prop to disable the dropdown
},ref) => {
  const [showRegularDropDown, setShowRegularDropDown] = useState(false);
  const dropdownRef = useRef(null);

  const handleToggle = () => {
    if (disabled) return; // Prevent opening when disabled
    setShowRegularDropDown((prev) => !prev);
  };

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowRegularDropDown(false);
    }
  };

  useEffect(() => {
    if (showRegularDropDown && !disabled) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [showRegularDropDown, disabled]);

  const handleOptionClick = (option) => {
    if (disabled) return; // Prevent option selection when disabled
    
    if (option.function) {
      option.function(); // Execute the function in the options object
    }
    if (setSelectedValue) {
      setSelectedValue(option.value); // Set the external state if provided
    }
    setShowRegularDropDown(false); // Close the dropdown
  };

  return (
    <div className={`RegularDropDownContainer ${SizeClassCorrespondance[size]} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      <div
        className={`RegularDropDownToggle ${showRegularDropDown ? "open" : ""} ${disabled ? 'disabled' : ''}`}
        onClick={handleToggle}
        ref={ref}
        style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
      >
        <span className="RegularDropDownText">
          {selectedValue ? options.filter(filter => filter.value == selectedValue).map(option => option.title) : placeholder}
        </span>
        <FontAwesomeIcon
          className={`RegularDropDownIndicator ${
            showRegularDropDown ? "rotate" : ""
          } ${disabled ? 'disabled' : ''}`}
          icon={faChevronDown}
        />
      </div>
      {!disabled && (
        <div
          className={`RegularDropDownList ${
            showRegularDropDown ? "open" : "closed"
          }`}
        >
          {options.length === 0 ? (
        <div className="RegularDropDownItem">{noOptionsMessage}</div>
          ) : (
            options.map((option, index) => (
              <div
                key={index}
                className={`RegularDropDownItem ${selectedValue && option.value && selectedValue == option.value ? "selected" : ""} ${option.disabled ? "disabled" : ""}`}
                onClick={() => {
                  if (!option.disabled) {
                    handleOptionClick(option);
                  }
                }}
              >
                {option.title}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
});

export const BorderedDropDown = forwardRef(({
  placeholder = "Actie",
  options = [],
  noOptionsMessage = "Geen acties mogelijk",
  onOptionSelect,
  label = null,
  selectedValue,
  setSelectedValue,
  size = 'l',
  error = false,
  errorTooltip = false,
  searchable = false, // New prop to enable search functionality
  searchPlaceholder = "Zoeken...", // Placeholder for search input
  disabled = false, // New prop to disable the dropdown
  maxLength = null
}, ref) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const dropdownRef = useRef(null);
  const hiddenInputRef = useRef(null);

  const handleToggle = () => {
    if (disabled) return; // Prevent opening when disabled
    
    setIsOpen((prev) => {
      const newState = !prev;
      if (newState && searchable) {
        // Focus hidden input when opening if searchable
        setTimeout(() => {
          hiddenInputRef.current?.focus();
        }, 0);
      }
      if (!newState) {
        // Clear search when closing
        setSearchTerm("");
        setIsTyping(false);
      }
      return newState;
    });
  };

  const handleOutsideClick = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsOpen(false);
      setSearchTerm("");
      setIsTyping(false);
    }
  };

  useEffect(() => {
    if (isOpen && !disabled) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, [isOpen, disabled]);

  const handleOptionClick = (option) => {
    if (disabled) return; // Prevent option selection when disabled
    
    if (option.onClick) {
      option.onClick();
    } else if (option.function) {
      option.function();
    }
    if (setSelectedValue) {
      setSelectedValue(option.value); // Set the external state if provided
    }
    setIsOpen(false); // Close the dropdown
    setSearchTerm(""); // Clear search
    setIsTyping(false);
  };

  const handleSearchChange = (e) => {
    if (disabled) return; // Prevent search when disabled
    
    const value = e.target.value;
    setSearchTerm(value);
    setIsTyping(value.length > 0);
  };

  const handleSearchKeyDown = (e) => {
    if (disabled) return; // Prevent search when disabled
    
    // Prevent closing dropdown when typing in search
    e.stopPropagation();
    
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm("");
      setIsTyping(false);
    }
    
    if (e.key === 'Enter') {
      // Select first filtered option on Enter
      if (filteredOptions.length > 0 && !filteredOptions[0].disabled) {
        handleOptionClick(filteredOptions[0]);
      }
    }

    if (e.key === 'Backspace' && searchTerm.length === 1) {
      // If backspacing the last character, switch back to normal mode
      setTimeout(() => {
        if (searchTerm === "") {
          setIsTyping(false);
        }
      }, 0);
    }
  };

  // Filter options based on search term - FIXED LOGIC
  const filteredOptions = React.useMemo(() => {
    // If searchable is false, always return all options
    if (!searchable) {
      return options;
    }
    
    // If searchable is true but no search term, return all options
    if (searchTerm.trim() === "") {
      return options;
    }
    
    // If there's a search term, filter the options
    return options.filter(option => 
      option.title && option.title.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchable, searchTerm, options]);

  // Get display text for selected value
  const getDisplayText = () => {
    if (selectedValue !== '' && selectedValue !== null && selectedValue !== undefined) {
      const selectedOption = options.find((option) => option.value === selectedValue);
      const text = selectedOption?.title || placeholder;
      
      if (maxLength && text.length > maxLength) {
        return text.substring(0, 11) + '...';
      }
      
      return text;
    }
    return placeholder;
  };

  return (
    <div className={`BorderedDropDownContainer ${SizeClassCorrespondance[size]} ${disabled ? 'disabled' : ''}`} ref={dropdownRef}>
      {label && <label className={disabled ? 'disabled' : ''}>{label}</label>}
      <div className={`input-container w-100`}>
        <div
          className={`BorderedDropDownToggle ${isOpen ? "open" : ""} ${error && "error"} ${isTyping ? "search-mode" : ""} ${disabled ? 'disabled' : ''}`}
          onClick={handleToggle}
          ref={ref}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          {isTyping && !disabled ? (
            <input
              type="text"
              placeholder={getDisplayText()}
              value={searchTerm}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyDown}
              className="BorderedDropDownToggleSearchInput"
              onClick={(e) => e.stopPropagation()}
              autoFocus
              disabled={disabled}
            />
          ) : (
            <span className={`BorderedDropDownText ${disabled ? 'disabled' : ''}`}>
              {getDisplayText()}
            </span>
          )}
          <FontAwesomeIcon
            className={`BorderedDropDownIndicator ${isOpen ? "rotate" : ""} ${disabled ? 'disabled' : ''}`}
            icon={faChevronDown}
          />
        </div>
        
        {/* Hidden input to capture keystrokes when not in typing mode */}
        {searchable && isOpen && !isTyping && !disabled && (
          <input
            ref={hiddenInputRef}
            type="text"
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            className="BorderedDropDownHiddenInput"
            autoFocus
            disabled={disabled}
          />
        )}

        {!disabled && (
          <div className={`BorderedDropDownList ${isOpen ? "open show" : "closed"}`}>
            {filteredOptions.length === 0 ? (
              <div className="BorderedDropDownItem">
                {searchable && searchTerm ? "Geen resultaten gevonden" : noOptionsMessage}
              </div>
            ) : (
              filteredOptions.map((option, index) => (
                <div
                  key={index}
                  className={`BorderedDropDownItem ${option.value && selectedValue && selectedValue === option.value ? "selected" : ""} ${option.disabled ? "disabled" : ""}`}
                  onClick={() => {
                    if (!option.disabled) {
                      handleOptionClick(option);
                    }
                  }}
                >
                  {option.title}
                </div>
              ))
            )}
          </div>
        )}
        {error && errorTooltip && <div className="tooltip">{errorTooltip}</div>}
      </div>
    </div>
  );
});